import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { LibraryBundleCard } from "@/components/bundles/LibraryBundleCard";
import { LibraryBundleModal } from "@/components/bundles/LibraryBundleModal";
import { Footer } from "@/components/layout/Footer";
import { GlobalHeroBackground } from "@/components/layout/GlobalHeroBackground";
import { Navbar } from "@/components/layout/Navbar";
import { useBundleList } from "@/hooks/useBundles";
import { useSEO } from "@/hooks/useSEO";
import { LibraryPreviewModal } from "@/components/tutorials/LibraryPreviewModal";
import { LibraryTutorialCard } from "@/components/tutorials/LibraryTutorialCard";
import { Button } from "@/components/ui/button";
import { useAddFreeCourse } from "@/hooks/useCheckout";
import { useCourseList } from "@/hooks/useCourses";
import { isPermissionExpired, useCoursePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/lib/auth";
import { useAppStore } from "@/lib/store";
import { cn } from "@/lib/utils";
import type { BundleWithCourses, CartBundleItem, CourseWithDetails } from "@/lib/schema";

const legacyBundleTagName = "Bundle";
const legacySignatureRoutineTagName = "Signature Routine";
const bundleTagName = "Signature Routine";

const libraryTags = [
  bundleTagName,
  "Low Movement (Accessible)",
  "Beginner",
  "Intermediate",
  "All-Male",
  "All-Female",
  "Coed",
  "Couples",
  "Groom",
  "Groomsmen",
  "Family of the Groom",
  "Bride",
  "Bridesmaids",
  "Family of the Bride",
];

const defaultInstructorImage =
  "https://cdn.prod.website-files.com/686229256a807206c1874545/687bcf08468fe4993282a9ec_Khusbu.avif";

const defaultInstructorBio =
  "Dancing is my first language. Every tutorial is designed to feel approachable, joyful, and performance-ready from the first watch.";

function normalizeLibraryTag(tag: string) {
  const normalizedTag = tag.trim();

  if (
    normalizedTag.toLowerCase() === legacyBundleTagName.toLowerCase() ||
    normalizedTag.toLowerCase() === legacySignatureRoutineTagName.toLowerCase()
  ) {
    return bundleTagName;
  }

  return normalizedTag;
}

function parseTags(searchParams: URLSearchParams) {
  return Array.from(
    new Set(
      searchParams
        .getAll("tags")
        .flatMap((value) => value.split(","))
        .map(normalizeLibraryTag)
        .filter(Boolean),
    ),
  );
}

function courseHasTag(course: CourseWithDetails, tag: string) {
  return course.category?.some((category) => category.name === tag) ?? false;
}

function bundleMatchesQuery(bundle: BundleWithCourses, query: string) {
  return (
    bundle.name.toLowerCase().includes(query) ||
    bundle.short_description?.toLowerCase().includes(query) ||
    bundle.long_description?.toLowerCase().includes(query) ||
    bundle.courses.some((course) => course.name.toLowerCase().includes(query))
  );
}

function sortCoursesByName(courses: CourseWithDetails[]) {
  return [...courses].sort((left, right) => left.name.localeCompare(right.name));
}

function getInstructorImage() {
  return defaultInstructorImage;
}

function getInstructorBio(course: CourseWithDetails) {
  return course.Instructor?.description?.trim() || defaultInstructorBio;
}

export function DanceLibraryPage() {
  useSEO({
    title: "Dance Library",
    description:
      "From throwback favorites to today's trends, choose from our tailored selection of Bollyfusion music.",
    canonical: "/dance-library",
  });

  const { user } = useAuth();
  const { data: courseRows, loading, error } = useCourseList();
  const { data: bundles, loading: bundleLoading, error: bundleError } = useBundleList();
  const { data: permissions, refetch: refetchPermissions } = useCoursePermissions();
  const { execute: addFreeCourse } = useAddFreeCourse();
  const {
    addToCart,
    cartItems,
    isBundleSaved,
    isSaved,
    setCartOpen,
    toggleSaved,
    toggleSavedBundle,
  } = useAppStore();
  const [searchParams] = useSearchParams();

  const courses = (courseRows ?? null) as CourseWithDetails[] | null;

  const [searchInput, setSearchInput] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);
  const [addingFreeCourseId, setAddingFreeCourseId] = useState<string | null>(null);
  const [tagScrollMetrics, setTagScrollMetrics] = useState({
    scrollLeft: 0,
    clientWidth: 1,
    scrollWidth: 1,
    trackWidth: 1,
  });

  const queryParam = searchParams.get("q") ?? searchParams.get("search") ?? "";
  const initialTagsKey = useMemo(() => parseTags(searchParams).join("\u001f"), [searchParams]);

  useEffect(() => {
    setSelectedTags(initialTagsKey ? initialTagsKey.split("\u001f") : []);
    setSearchInput(queryParam);
  }, [initialTagsKey, queryParam]);

  const checkoutStatus = searchParams.get("checkout");

  const activePermissionIds = useMemo(() => {
    return new Set(
      (permissions ?? [])
        .filter((permission) => !isPermissionExpired(permission))
        .map((permission) => permission.course_id.id),
      );
  }, [permissions]);

  const bundleItems = cartItems.filter((item): item is CartBundleItem => item.type === "bundle");
  const cartBundleIds = useMemo(
    () => new Set(bundleItems.map((item) => item.bundle_id)),
    [bundleItems],
  );
  const cartBundleCourseIds = useMemo(
    () => new Set(bundleItems.flatMap((item) => item.course_ids)),
    [bundleItems],
  );
  const bundleTagSelected = selectedTags.includes(bundleTagName);
  const tutorialTags = useMemo(
    () => selectedTags.filter((tag) => tag !== bundleTagName),
    [selectedTags],
  );

  const filteredCourses = useMemo(() => {
    if (!courses) return [];
    if (bundleTagSelected) return [];

    const query = searchInput.trim().toLowerCase();

    return [...courses]
      .filter((course) => {
        const searchMatch =
          query.length === 0 ||
          course.name.toLowerCase().includes(query) ||
          course.short_description?.toLowerCase().includes(query) ||
          course.long_description?.toLowerCase().includes(query);
        const tagMatch =
          tutorialTags.length === 0 ||
          tutorialTags.every((tag) => courseHasTag(course, tag));

        return searchMatch && tagMatch;
      });
  }, [bundleTagSelected, courses, searchInput, tutorialTags]);

  const filteredBundles = useMemo(() => {
    if (!bundles) return [];

    const query = searchInput.trim().toLowerCase();

    return [...bundles]
      .filter((bundle) => {
        const searchMatch = query.length === 0 || bundleMatchesQuery(bundle, query);
        const tagMatch = selectedTags.length === 0 || bundleTagSelected;

        return searchMatch && tagMatch;
      })
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [bundleTagSelected, bundles, searchInput, selectedTags.length]);

  const freeCourses = useMemo(
    () => sortCoursesByName(filteredCourses.filter((course) => Boolean(course.is_free))),
    [filteredCourses],
  );
  const popularPaidCourses = useMemo(
    () => sortCoursesByName(filteredCourses.filter((course) => !course.is_free && Boolean(course.most_popular))),
    [filteredCourses],
  );
  const regularPaidCourses = useMemo(
    () => sortCoursesByName(filteredCourses.filter((course) => !course.is_free && !course.most_popular)),
    [filteredCourses],
  );

  const selectedCourse =
    selectedCourseId && courses
      ? courses.find((course) => course.id === selectedCourseId) ?? null
      : null;

  const selectedBundle =
    selectedBundleId && bundles
      ? bundles.find((bundle) => bundle.id === selectedBundleId) ?? null
      : null;

  const loginHref = `/app/auth/login?redirect=${encodeURIComponent("/dance-library")}`;

  const handleAddToCart = (course: CourseWithDetails) => {
    addToCart({
      type: "course",
      course_id: course.id,
      name: course.name,
      price: course.price,
    });
    setCartOpen(true);
  };

  const handleAddBundleToCart = (bundle: BundleWithCourses) => {
    addToCart({
      type: "bundle",
      bundle_id: bundle.id,
      name: bundle.name,
      price: bundle.price,
      thumbnail_url: bundle.thumbnail_url ?? undefined,
      course_ids: bundle.courses.map((course) => course.id),
      course_count: bundle.courses.length,
    });
    setCartOpen(true);
  };

  const handleAddFree = async (courseId: string) => {
    setAddingFreeCourseId(courseId);
    await addFreeCourse(courseId);
    await refetchPermissions();
    setAddingFreeCourseId(null);
  };

  const toggleTag = (tag: string) => {
    const normalizedTag = normalizeLibraryTag(tag);

    setSelectedTags((current) => {
      if (normalizedTag === bundleTagName) {
        return current.includes(bundleTagName) ? current.filter((value) => value !== bundleTagName) : [bundleTagName];
      }

      const withoutBundle = current.filter((value) => value !== bundleTagName);
      return withoutBundle.includes(normalizedTag)
        ? withoutBundle.filter((value) => value !== normalizedTag)
        : [...withoutBundle, normalizedTag];
    });
  };

  const getBundleOwnedCourseCount = useCallback(
    (bundle: BundleWithCourses) => bundle.courses.filter((course) => activePermissionIds.has(course.id)).length,
    [activePermissionIds],
  );

  const isBundleOwned = useCallback(
    (bundle: BundleWithCourses) => bundle.courses.length > 0 && bundle.courses.every((course) => activePermissionIds.has(course.id)),
    [activePermissionIds],
  );

  const hasBundleCartConflict = useCallback(
    (bundle: BundleWithCourses) => {
      const bundleCourseIds = bundle.courses.map((course) => course.id);
      return bundleItems.some(
        (item) =>
          item.bundle_id !== bundle.id &&
          item.course_ids.some((courseId) => bundleCourseIds.includes(courseId)),
      );
    },
    [bundleItems],
  );

  // Drag-to-scroll for tags
  const tagScrollRef = useRef<HTMLDivElement>(null);
  const tagScrollbarTrackRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ isDown: false, startX: 0, scrollLeft: 0, didDrag: false });
  const tagScrollbarDragState = useRef({
    isDragging: false,
    pointerId: -1,
    startClientX: 0,
    startScrollLeft: 0,
  });

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    const el = tagScrollRef.current;
    if (!el) return;
    dragState.current = { isDown: true, startX: e.clientX, scrollLeft: el.scrollLeft, didDrag: false };
    el.style.cursor = "grabbing";
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    const ds = dragState.current;
    if (!ds.isDown) return;
    e.preventDefault();
    const dx = e.clientX - ds.startX;
    if (Math.abs(dx) > 3) ds.didDrag = true;
    tagScrollRef.current!.scrollLeft = ds.scrollLeft - dx;
  }, []);

  const onMouseUp = useCallback(() => {
    if (!tagScrollRef.current) return;
    tagScrollRef.current.style.cursor = "";
    dragState.current.isDown = false;
  }, []);

  const syncTagScrollMetrics = useCallback(() => {
    const element = tagScrollRef.current;
    if (!element) return;

    const trackWidth = tagScrollbarTrackRef.current?.clientWidth ?? element.clientWidth;

    setTagScrollMetrics((current) => {
      const next = {
        scrollLeft: element.scrollLeft,
        clientWidth: element.clientWidth,
        scrollWidth: element.scrollWidth,
        trackWidth,
      };

      if (
        current.scrollLeft === next.scrollLeft &&
        current.clientWidth === next.clientWidth &&
        current.scrollWidth === next.scrollWidth &&
        current.trackWidth === next.trackWidth
      ) {
        return current;
      }

      return next;
    });
  }, []);

  const getTagScrollbarThumbWidth = useCallback((trackWidth: number) => {
    return Math.min(Math.max(trackWidth * 0.12, 64), 104, trackWidth);
  }, []);

  useEffect(() => {
    const element = tagScrollRef.current;
    const track = tagScrollbarTrackRef.current;
    if (!element) return;

    syncTagScrollMetrics();

    element.addEventListener("scroll", syncTagScrollMetrics, { passive: true });
    window.addEventListener("resize", syncTagScrollMetrics);

    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(syncTagScrollMetrics);

    resizeObserver?.observe(element);
    if (track) {
      resizeObserver?.observe(track);
    }

    return () => {
      element.removeEventListener("scroll", syncTagScrollMetrics);
      window.removeEventListener("resize", syncTagScrollMetrics);
      resizeObserver?.disconnect();
    };
  }, [syncTagScrollMetrics]);

  const handleTagScrollbarPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const element = tagScrollRef.current;
    if (!element) return;

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);

    tagScrollbarDragState.current = {
      isDragging: true,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startScrollLeft: element.scrollLeft,
    };

    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
  }, []);

  const handleTagScrollbarPointerMove = useCallback((event: PointerEvent) => {
    const drag = tagScrollbarDragState.current;
    const element = tagScrollRef.current;
    const track = tagScrollbarTrackRef.current;

    if (!drag.isDragging || drag.pointerId !== event.pointerId || !element || !track) return;

    if (event.cancelable) {
      event.preventDefault();
    }

    const scrollRange = element.scrollWidth - element.clientWidth;
    if (scrollRange <= 0) return;

    const trackWidth = track.clientWidth;
    const thumbWidth = getTagScrollbarThumbWidth(trackWidth);
    const thumbTravel = Math.max(1, trackWidth - thumbWidth);
    const deltaX = event.clientX - drag.startClientX;
    const scrollDelta = (deltaX / thumbTravel) * scrollRange;

    element.scrollLeft = Math.min(
      Math.max(drag.startScrollLeft + scrollDelta, 0),
      scrollRange,
    );
  }, []);

  const handleTagScrollbarPointerUp = useCallback((event: PointerEvent) => {
    if (tagScrollbarDragState.current.pointerId !== event.pointerId) return;

    tagScrollbarDragState.current = {
      isDragging: false,
      pointerId: -1,
      startClientX: 0,
      startScrollLeft: 0,
    };

    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  useEffect(() => {
    window.addEventListener("pointermove", handleTagScrollbarPointerMove);
    window.addEventListener("pointerup", handleTagScrollbarPointerUp);
    window.addEventListener("pointercancel", handleTagScrollbarPointerUp);

    return () => {
      window.removeEventListener("pointermove", handleTagScrollbarPointerMove);
      window.removeEventListener("pointerup", handleTagScrollbarPointerUp);
      window.removeEventListener("pointercancel", handleTagScrollbarPointerUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [handleTagScrollbarPointerMove, handleTagScrollbarPointerUp]);

  const tagScrollRange = Math.max(0, tagScrollMetrics.scrollWidth - tagScrollMetrics.clientWidth);
  const showTagScrollbar = tagScrollRange > 1;
  const tagScrollbarTrackWidth = Math.max(tagScrollMetrics.trackWidth, 1);
  const tagScrollbarThumbWidth = showTagScrollbar
    ? getTagScrollbarThumbWidth(tagScrollbarTrackWidth)
    : tagScrollbarTrackWidth;
  const tagScrollbarThumbTravel = Math.max(tagScrollbarTrackWidth - tagScrollbarThumbWidth, 0);
  const tagScrollbarThumbOffset = showTagScrollbar
    ? Math.min(
        Math.max((tagScrollMetrics.scrollLeft / tagScrollRange) * tagScrollbarThumbTravel, 0),
        tagScrollbarThumbTravel,
      )
    : 0;

  return (
    <div className="page-wrapper landing-page relative">
      <Navbar />
      <GlobalHeroBackground />

      <main className="landing-page__main main-wrapper">
        <section className="relative px-6 py-[6.25rem] max-[767px]:pb-[3.75rem] max-[767px]:pt-16 max-[479px]:px-4">
          <div className="relative z-[1] mx-auto max-w-[48rem] text-center">
            <h1 className="mx-auto max-w-[17ch] font-display text-[clamp(2rem,1.5714rem+2.1429vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-[#1f0a18]">
              Browse Our Collection of <span className="text-[#762a5c]">Tutorials</span>
            </h1>
            <label className="mx-auto mt-8 flex w-[min(100%,25rem)] items-center gap-[0.8rem] rounded-full border-[0.09rem] border-[#eee] bg-[rgba(255,255,255,0.95)] px-[1.2rem] py-[0.95rem]">
              <Search className="h-5 w-5 text-[#d5c9d2]" />
              <input
                type="text"
                value={searchInput}
                placeholder="Search our tutorials"
                className="w-full border-0 bg-transparent text-[0.98rem] text-[#4b3c43] outline-0 placeholder:text-[#aaa]"
                data-testid="library-search-input"
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </label>
          </div>
        </section>

        {checkoutStatus === "success" ? (
          <div className="mt-5 rounded-[1.1rem] bg-[#ecfff3] px-[1.1rem] py-[0.95rem] text-[0.95rem] font-semibold text-[#1f7a45]">
            Payment successful. Your tutorial access is being refreshed.
          </div>
        ) : null}

        {checkoutStatus === "failed" ? (
          <div className="mt-5 rounded-[1.1rem] bg-[#fff3f0] px-[1.1rem] py-[0.95rem] text-[0.95rem] font-semibold text-[#a3533d]">
            Checkout did not complete. Please try again.
          </div>
        ) : null}

        <section className="mx-auto mt-12 w-full max-w-[90rem] px-[var(--site-margin)]">
          <div className="text-[18px] font-medium leading-[27px]">Filter by tags</div>

          <div className="relative -mx-[var(--site-margin)] mt-5">
            <div
              ref={tagScrollRef}
              className="flex cursor-grab flex-nowrap gap-4 overflow-x-auto px-[var(--site-margin)] pb-2 select-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden max-[991px]:gap-3"
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
            >
              {libraryTags.map((tag) => {
                const active = selectedTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    className={cn(
                      "flex-none whitespace-nowrap rounded-full border border-pink-dark bg-white px-6 py-4 text-neutral-black hover:text-pink-dark",
                      active && "border-[#f2a435] bg-[#f2a435] text-white hover:text-white",
                    )}
                    onClick={() => {
                      if (dragState.current.didDrag) return;
                      toggleTag(tag);
                    }}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>

            <div className="px-[var(--site-margin)]">
              <div
                ref={tagScrollbarTrackRef}
                className={cn(
                  "relative mt-2 flex h-4 items-center transition-opacity duration-200",
                  showTagScrollbar ? "opacity-100" : "pointer-events-none opacity-0",
                )}
              >
                <div className="h-[0.24rem] w-full rounded-full bg-[#eee4f3]" />
                <div
                  className="absolute inset-y-[-0.4rem] flex touch-none items-center rounded-full cursor-grab active:cursor-grabbing"
                  onPointerDown={handleTagScrollbarPointerDown}
                  style={{
                    left: `${tagScrollbarThumbOffset}px`,
                    width: `${tagScrollbarThumbWidth}px`,
                  }}
                >
                  <div className="h-[0.24rem] w-full rounded-full bg-[#c5acd2] shadow-[0_1px_3px_rgba(118,42,92,0.08)]" />
                </div>
              </div>
            </div>
          </div>

          {selectedTags.length > 0 ? (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {selectedTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className="inline-flex items-center gap-[0.65rem] rounded-full border-[1.5px] border-[#b7789a] bg-[#762a5c] px-4 py-[0.7rem] text-[1rem] font-semibold leading-none text-white whitespace-nowrap transition-transform duration-[180ms] ease-in-out hover:-translate-y-px"
                  onClick={() => toggleTag(tag)}
                >
                  <span>{tag}</span>
                  <span className="inline-flex h-[1.15rem] w-[1.15rem] items-center justify-center rounded-full bg-white/20">
                    <X className="h-3 w-3" />
                  </span>
                </button>
              ))}

              <Button
                type="button"
                variant="outline"
                className="rounded-round border-[#cfbdd0] text-[#762a5c] hover:bg-[#762a5c] hover:text-white"
                onClick={() => setSelectedTags([])}
              >
                Clear filters
              </Button>
            </div>
          ) : null}
        </section>

        <section className="mx-auto mt-14 w-full max-w-[90rem] px-[var(--site-margin)] pb-16 max-[767px]:mt-8">
          {loading || bundleLoading ? (
            <div className="grid gap-x-6 gap-y-12 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  className="h-[27rem] animate-pulse rounded-[1.75rem] bg-white shadow-[0_24px_55px_rgba(74,25,60,0.07)]"
                />
              ))}
            </div>
          ) : error && bundleError ? (
            <div className="rounded-[1.5rem] border border-dashed border-[#d9c2cf] bg-white px-6 py-14 text-center text-[#762a5c]">
              Failed to load the dance library. Please try again later.
            </div>
          ) : freeCourses.length === 0 && filteredBundles.length === 0 && popularPaidCourses.length === 0 && regularPaidCourses.length === 0 ? (
            <div className="rounded-[1.5rem] border border-dashed border-[#d9c2cf] bg-white px-6 py-14 text-center text-[#5f5a5d]">
              {bundleTagSelected
                ? "No signature routines matched this search. Try another keyword."
                : "No tutorials matched this search. Try another tag combination."}
            </div>
          ) : (
            <div className="grid gap-x-6 gap-y-12 md:grid-cols-2 xl:grid-cols-3">
              {freeCourses.map((course) => {
                const isOwned = activePermissionIds.has(course.id);
                const saved = isSaved(course.id);
                const inCart = cartItems.some(
                  (item) => item.type === "course" && item.course_id === course.id,
                ) || cartBundleCourseIds.has(course.id);
                const addingFree = addingFreeCourseId === course.id;

                return (
                  <LibraryTutorialCard
                    key={course.id}
                    course={course}
                    instructorImage={getInstructorImage()}
                    isLoggedIn={Boolean(user)}
                    isOwned={isOwned}
                    isSaved={saved}
                    inCart={inCart}
                    addingFree={addingFree}
                    loginHref={loginHref}
                    onAddFree={handleAddFree}
                    onAddToCart={handleAddToCart}
                    onOpenPreview={() => setSelectedCourseId(course.id)}
                    onToggleSaved={() => toggleSaved(course.id)}
                  />
                );
              })}

              {filteredBundles.map((bundle) => {
                const isOwned = isBundleOwned(bundle);
                const saved = isBundleSaved(bundle.id);
                const inCart = cartBundleIds.has(bundle.id);
                const hasCartConflict = !inCart && hasBundleCartConflict(bundle);
                const ownedCourseCount = getBundleOwnedCourseCount(bundle);

                return (
                  <LibraryBundleCard
                    key={bundle.id}
                    bundle={bundle}
                    instructorImage={getInstructorImage()}
                    isLoggedIn={Boolean(user)}
                    isOwned={isOwned}
                    isSaved={saved}
                    inCart={inCart}
                    hasCartConflict={hasCartConflict}
                    ownedCourseCount={ownedCourseCount}
                    loginHref={loginHref}
                    onAddToCart={handleAddBundleToCart}
                    onOpenPreview={() => setSelectedBundleId(bundle.id)}
                    onToggleSaved={() => toggleSavedBundle(bundle.id)}
                  />
                );
              })}

              {popularPaidCourses.map((course) => {
                const isOwned = activePermissionIds.has(course.id);
                const saved = isSaved(course.id);
                const inCart = cartItems.some(
                  (item) => item.type === "course" && item.course_id === course.id,
                ) || cartBundleCourseIds.has(course.id);
                const addingFree = addingFreeCourseId === course.id;

                return (
                  <LibraryTutorialCard
                    key={course.id}
                    course={course}
                    instructorImage={getInstructorImage()}
                    isLoggedIn={Boolean(user)}
                    isOwned={isOwned}
                    isSaved={saved}
                    inCart={inCart}
                    addingFree={addingFree}
                    loginHref={loginHref}
                    onAddFree={handleAddFree}
                    onAddToCart={handleAddToCart}
                    onOpenPreview={() => setSelectedCourseId(course.id)}
                    onToggleSaved={() => toggleSaved(course.id)}
                  />
                );
              })}

              {regularPaidCourses.map((course) => {
                const isOwned = activePermissionIds.has(course.id);
                const saved = isSaved(course.id);
                const inCart = cartItems.some(
                  (item) => item.type === "course" && item.course_id === course.id,
                ) || cartBundleCourseIds.has(course.id);
                const addingFree = addingFreeCourseId === course.id;

                return (
                  <LibraryTutorialCard
                    key={course.id}
                    course={course}
                    instructorImage={getInstructorImage()}
                    isLoggedIn={Boolean(user)}
                    isOwned={isOwned}
                    isSaved={saved}
                    inCart={inCart}
                    addingFree={addingFree}
                    loginHref={loginHref}
                    onAddFree={handleAddFree}
                    onAddToCart={handleAddToCart}
                    onOpenPreview={() => setSelectedCourseId(course.id)}
                    onToggleSaved={() => toggleSaved(course.id)}
                  />
                );
              })}
            </div>
          )}
        </section>
      </main>

      <Footer />

      {selectedCourse ? (
        <LibraryPreviewModal
          course={selectedCourse}
          instructorBio={getInstructorBio(selectedCourse)}
          instructorImage={getInstructorImage()}
          isLoggedIn={Boolean(user)}
          isOwned={activePermissionIds.has(selectedCourse.id)}
          isSaved={isSaved(selectedCourse.id)}
          inCart={cartItems.some((item) => item.type === "course" && item.course_id === selectedCourse.id) || cartBundleCourseIds.has(selectedCourse.id)}
          addingFree={addingFreeCourseId === selectedCourse.id}
          loginHref={loginHref}
          onAddFree={handleAddFree}
          onAddToCart={handleAddToCart}
          onClose={() => setSelectedCourseId(null)}
          onToggleSaved={() => toggleSaved(selectedCourse.id)}
        />
      ) : null}

      {selectedBundle ? (
        <LibraryBundleModal
          bundle={selectedBundle}
          instructorImage={getInstructorImage()}
          isLoggedIn={Boolean(user)}
          isOwned={isBundleOwned(selectedBundle)}
          isSaved={isBundleSaved(selectedBundle.id)}
          inCart={cartBundleIds.has(selectedBundle.id)}
          hasCartConflict={!cartBundleIds.has(selectedBundle.id) && hasBundleCartConflict(selectedBundle)}
          ownedCourseIds={activePermissionIds}
          loginHref={loginHref}
          onAddToCart={handleAddBundleToCart}
          onClose={() => setSelectedBundleId(null)}
          onToggleSaved={() => toggleSavedBundle(selectedBundle.id)}
        />
      ) : null}
    </div>
  );
}
